import { useState } from 'react';
import { Printer, X } from 'lucide-react';
import { useApi } from '../../lib/api.js';

// =============================================================================
// PrintBookingsButton — print / save-as-PDF the bookings matching the current
// filters on the bookings list page.
// =============================================================================
// Opens a small options popup ("Client copy" toggle), then fetches EVERY
// booking matching the filters (all pages, not just the one on screen),
// builds a clean A4 page laid out like the list — one booking under another,
// grouped by day — and opens the browser print dialog. From there: print on
// paper or choose "Save as PDF".
//
// Client copy hides internal bits: status, chauffeur / vehicle reg, prices.
//
// Props:
//   buildQueryString(filters) — the list page's own helper (same filters)
//   filters                   — the list page's current filter state
//   isOwner                   — prices only ever shown to owners
//   statusLabels              — { status: label } (the page's STATUS_CONFIG)
// =============================================================================

const MAX_BOOKINGS = 1000;   // safety cap for one printout
const PAGE_SIZE = 200;

function esc(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function ymd(date) {
    if (!date) return '';
    if (date instanceof Date) return date.toISOString().slice(0, 10);
    return String(date).slice(0, 10);
}

function dayHeading(dateStr) {
    const d = new Date(`${dateStr}T12:00:00`);
    if (isNaN(d.getTime())) return dateStr || 'No date';
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function shortDate(dateStr) {
    const d = new Date(`${dateStr}T12:00:00`);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function buildHtml({ bookings, clientCopy, isOwner, filters, statusLabels, truncated }) {
    // Group by pickup date, keep the list's order inside each day.
    const groups = new Map();
    for (const b of bookings) {
        const key = ymd(b.pickup_date) || 'none';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(b);
    }

    const showPrices = isOwner && !clientCopy;
    const customerNames = [...new Set(bookings.map((b) => b.customer_name).filter(Boolean))];
    const titleParts = ['Bookings'];
    if (customerNames.length === 1) titleParts.push(customerNames[0]);
    const range = filters.date_from && filters.date_to
        ? (filters.date_from === filters.date_to
            ? shortDate(filters.date_from)
            : `${shortDate(filters.date_from)} – ${shortDate(filters.date_to)}`)
        : '';

    let total = 0;
    let priced = 0;

    const sections = [...groups.entries()].map(([day, list]) => {
        const rows = list.map((b) => {
            const passenger = b.passenger_name || b.pax_name || '';
            const vehicle = b.vehicle_class_label || b.vehicle_class || '';
            const net = b.customer_net != null ? Number(b.customer_net) : null;
            if (showPrices && net != null && net > 0) { total += net; priced++; }
            const status = statusLabels[b.status] || b.status || '';
            const time = b.pickup_time ? String(b.pickup_time).slice(0, 5) : '—';
            const until = b.mode === 'hourly' && b.final_time ? `until ${esc(String(b.final_time).slice(0, 5))}` : '';
            const isCancelled = b.status === 'cancelled' || b.status === 'no_show';

            const line1 = [
                `<span class="ref">${esc(b.ride_ref)}</span>`,
                customerNames.length === 1 ? '' : esc(b.customer_name || ''),
                passenger ? `Pax: ${esc(passenger)}` : '',
                b.pax_count ? `${esc(b.pax_count)} pax` : '',
            ].filter(Boolean).join('<span class="dot">·</span>');

            const line3Parts = [esc(vehicle || '—')];
            if (b.mode === 'hourly') line3Parts.push('Hourly');
            if (!clientCopy) {
                line3Parts.push(b.chauffeur_name
                    ? `${esc(b.chauffeur_name)}${b.vehicle_registration ? ` · ${esc(b.vehicle_registration)}` : ''}`
                    : '<em>Not allocated</em>');
            }
            if (b.flight_number) line3Parts.push(`Flight ${esc(b.flight_number)}`);

            return `
            <div class="row${isCancelled ? ' cancelled' : ''}">
                <div class="when">
                    <div class="time">${esc(time)}</div>
                    ${until ? `<div class="sub">${until}</div>` : ''}
                </div>
                <div class="what">
                    <div class="l1">${line1}</div>
                    <div class="l2">${esc(b.pickup_address || '')}<span class="arrow">→</span>${esc(b.dropoff_address || '')}</div>
                    <div class="l3">${line3Parts.join('<span class="dot">·</span>')}</div>
                </div>
                <div class="side">
                    ${!clientCopy ? `<div class="status">${esc(status)}</div>` : (isCancelled ? `<div class="status">${esc(status)}</div>` : '')}
                    ${showPrices && net != null && net > 0 ? `<div class="price">£${net.toFixed(2)}<span> ex VAT</span></div>` : ''}
                </div>
            </div>`;
        }).join('');

        return `
        <section>
            <h2>${esc(day === 'none' ? 'No date' : dayHeading(day))}<span>${list.length} booking${list.length === 1 ? '' : 's'}</span></h2>
            ${rows}
        </section>`;
    }).join('');

    const printed = new Date().toLocaleString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    return `<!doctype html>
<html><head><meta charset="utf-8">
<title>${esc(titleParts.join(' – '))}${range ? ` – ${esc(range)}` : ''}</title>
<style>
    @page { size: A4; margin: 14mm 12mm; }
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif;
           color: #111; font-size: 10.5pt; margin: 0; }
    header { display: flex; justify-content: space-between; align-items: flex-end;
             border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 14px; }
    .brand { font-size: 9pt; letter-spacing: .12em; text-transform: uppercase; color: #555; }
    h1 { font-size: 16pt; margin: 2px 0 0; }
    .meta { text-align: right; font-size: 9pt; color: #555; line-height: 1.5; }
    section { margin-bottom: 10px; }
    h2 { font-size: 10.5pt; margin: 14px 0 4px; padding: 4px 0; border-bottom: 1px solid #bbb;
         display: flex; justify-content: space-between; break-after: avoid; }
    h2 span { font-weight: normal; color: #666; font-size: 9pt; }
    .row { display: grid; grid-template-columns: 62px 1fr auto; gap: 10px; padding: 7px 0;
           border-bottom: 1px solid #e3e3e3; break-inside: avoid; }
    .row.cancelled { color: #888; }
    .row.cancelled .l2 { text-decoration: line-through; }
    .time { font-weight: 700; font-size: 11pt; }
    .sub { font-size: 8.5pt; color: #666; }
    .l1 { font-size: 9.5pt; }
    .l2 { font-size: 10.5pt; margin: 2px 0; }
    .l3 { font-size: 9pt; color: #555; }
    .ref { font-family: Menlo, Consolas, monospace; font-size: 8.5pt; color: #444; }
    .dot { color: #999; margin: 0 5px; }
    .arrow { color: #888; margin: 0 6px; }
    .side { text-align: right; white-space: nowrap; }
    .status { font-size: 8.5pt; border: 1px solid #bbb; border-radius: 3px; padding: 1px 5px; display: inline-block; }
    .price { font-weight: 600; margin-top: 4px; }
    .price span { font-weight: normal; font-size: 8pt; color: #666; }
    .totals { margin-top: 14px; padding-top: 8px; border-top: 2px solid #111; display: flex;
              justify-content: space-between; font-size: 10pt; }
    .warn { margin-top: 10px; font-size: 9pt; color: #a33; }
    footer { margin-top: 18px; font-size: 8.5pt; color: #777; text-align: center; }
    em { color: #888; }
</style></head>
<body>
    <header>
        <div>
            <div class="brand">Lanz CTS</div>
            <h1>${esc(titleParts.join(' · '))}</h1>
        </div>
        <div class="meta">
            ${range ? `${esc(range)}<br>` : ''}
            ${bookings.length} booking${bookings.length === 1 ? '' : 's'}<br>
            Printed ${esc(printed)}
        </div>
    </header>
    ${bookings.length === 0 ? '<p>No bookings match these filters.</p>' : sections}
    ${showPrices && priced > 0 ? `
    <div class="totals">
        <span>${priced} priced booking${priced === 1 ? '' : 's'}</span>
        <strong>Total £${total.toFixed(2)} ex VAT</strong>
    </div>` : ''}
    ${truncated ? `<div class="warn">Only the first ${MAX_BOOKINGS} bookings are included — narrow the filters to print the rest.</div>` : ''}
    <footer>Lanz CTS · +44 (0)20 8123 8337 · info@lanzcts.com</footer>
</body></html>`;
}

export function PrintBookingsButton({ buildQueryString, filters, isOwner, statusLabels }) {
    const api = useApi();
    const [open, setOpen] = useState(false);
    const [clientCopy, setClientCopy] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    async function handlePrint() {
        setError(null);
        // Open the window straight away (inside the click) so popup blockers
        // allow it; fill it once the bookings have loaded.
        const win = window.open('', '_blank');
        if (!win) {
            setError('Your browser blocked the print window — allow pop-ups for dispatch.lanzcts.com and try again.');
            return;
        }
        win.document.write('<p style="font-family:sans-serif;padding:24px">Preparing bookings…</p>');

        setBusy(true);
        try {
            const all = [];
            let offset = 0;
            let total = Infinity;
            while (all.length < total && all.length < MAX_BOOKINGS) {
                const qs = buildQueryString({ ...filters, limit: PAGE_SIZE, offset });
                const ownerQs = isOwner ? `${qs}&include_totals=true` : qs;
                const data = await api.get(`/bookings?${ownerQs}`);
                const page = data?.bookings || [];
                total = data?.total ?? page.length;
                all.push(...page);
                if (page.length === 0) break;
                offset += page.length;
            }
            const truncated = total > MAX_BOOKINGS;
            const html = buildHtml({
                bookings: all.slice(0, MAX_BOOKINGS),
                clientCopy,
                isOwner,
                filters,
                statusLabels,
                truncated,
            });
            win.document.open();
            win.document.write(html);
            win.document.close();
            win.focus();
            // Give the new page a moment to lay out before the print dialog.
            setTimeout(() => win.print(), 300);
            setOpen(false);
        } catch (err) {
            win.close();
            setError(err?.message || 'Could not load bookings to print');
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
            <button
                type="button"
                onClick={() => { setError(null); setOpen(true); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-line bg-white text-ink text-sm font-medium hover:bg-canvas transition-colors"
            >
                <Printer className="w-4 h-4" />
                Print
            </button>

            {open && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
                            <h3 className="text-sm font-semibold text-ink">Print bookings</h3>
                            <button type="button" onClick={() => setOpen(false)} disabled={busy} className="p-1 text-muted hover:text-ink">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="px-5 py-4 space-y-3 text-sm">
                            <p className="text-muted">
                                Prints every booking matching the current filters (all pages), grouped by day.
                                In the print window choose your printer, or <strong>Save as PDF</strong>.
                            </p>
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input type="checkbox" className="mt-0.5" checked={clientCopy} onChange={(e) => setClientCopy(e.target.checked)} />
                                <span>
                                    <span className="text-ink font-medium">Client copy</span>
                                    <span className="block text-xs text-muted">Hides status, chauffeur, vehicle reg and prices.</span>
                                </span>
                            </label>
                            {error && <div className="text-xs text-red-600">{error}</div>}
                        </div>
                        <div className="px-5 py-3 border-t border-line flex justify-end gap-2">
                            <button type="button" onClick={() => setOpen(false)} disabled={busy} className="px-3 py-1.5 text-sm text-muted hover:text-ink">
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handlePrint}
                                disabled={busy}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-ink text-white text-sm font-semibold disabled:opacity-60"
                            >
                                <Printer className="w-4 h-4" />
                                {busy ? 'Preparing…' : 'Print'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
